import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import { threeAnswers } from '@/mocks/homeData';

const tabMeta = [
  {
    highlights: [
      { num: '5', label: '评价维度', color: 'primary' },
      { num: '100+', label: '细分指标', color: 'accent' },
    ],
    items: [
      { num: '01', title: '政治方向', desc: '教学目标与内容符合思政课育人方向，确保教学内容始终围绕正确的政治立场与价值导向展开', icon: 'ri-compass-3-line', accent: true },
      { num: '02', title: '理论阐释', desc: '理论讲解准确、系统、有深度，帮助学生建立完整的知识体系与理论框架', icon: 'ri-book-open-line' },
      { num: '03', title: '案例使用', desc: '案例选取恰当，与理论结合紧密，做到理论联系实际、案例支撑观点', icon: 'ri-archive-drawer-line' },
      { num: '04', title: '互动设计', desc: '师生互动有效，学生参与度高，课堂讨论能够激发深度思考', icon: 'ri-group-line' },
      { num: '05', title: '价值引导', desc: '价值引领自然融入教学过程，润物细无声地实现立德树人根本任务', icon: 'ri-heart-pulse-line' },
    ],
  },
  {
    highlights: [
      { num: '7000', label: '实录课程', color: 'primary' },
      { num: '1.2万', label: '课例标记', color: 'accent' },
    ],
    items: [
      { num: '01', title: '课程投喂', desc: '7000节实录课程进入系统，覆盖不同层次、不同风格的教学场景', icon: 'ri-upload-cloud-line', accent: true },
      { num: '02', title: '专家标记', desc: '1.2万条课例数据由领域专家人工标注，建立高质量训练基准', icon: 'ri-mark-pen-line' },
      { num: '03', title: '模型训练', desc: '思政课专用评价模型持续优化，针对学科特点进行定向训练', icon: 'ri-brain-line' },
      { num: '04', title: '人机校准', desc: '最优与最差课程人机比对验证，确保模型判断与专家一致', icon: 'ri-shuffle-line' },
    ],
  },
  {
    highlights: [
      { num: '93.8%', label: '关键判断吻合', color: 'primary' },
      { num: '16.7万', label: '有效评课数据', color: 'accent' },
    ],
    items: [
      { num: '01', title: '历史样本校准', desc: '6年16.77万个有效评课数据回测，验证模型在不同时间段的稳定性', icon: 'ri-history-line', accent: true },
      { num: '02', title: '人机比对验证', desc: '最优与最差课程人机吻合度93.8%，关键判断高度一致', icon: 'ri-user-voice-line' },
      { num: '03', title: '专家抽检复核', desc: '定期邀请领域专家抽检模型输出结果，确保质量可控', icon: 'ri-shield-check-line' },
      { num: '04', title: '持续迭代优化', desc: '根据反馈不断校准评价标准，模型随实践持续进化', icon: 'ri-refresh-line' },
    ],
  },
];

export default function Section4Answers({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [activeTab, setActiveTab] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  const handleTab = (i: number) => {
    if (i !== activeTab) {
      setActiveTab(i);
      setAnimKey((k) => k + 1);
    }
  };

  const answer = threeAnswers[activeTab];
  const meta = tabMeta[activeTab];
  const totalItems = meta.items.length;
  const isFiveItems = totalItems === 5;

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-28 right-24 w-72 h-72 rounded-full bg-gold-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-20 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50/80 border border-primary-200/40 text-xs font-body font-semibold text-primary-600 tracking-[0.1em] mb-3 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <i className="ri-shield-check-line text-sm" />
            可信答案
          </span>
          <h2
            className={`font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            有标准、有训练、<span className="text-primary-600">有验证</span>
          </h2>
          <p
            className={`text-sm md:text-base text-foreground-500 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            它不是"会说话"的模型，而是"按标准评价、按样本训练、按结果验证"的专业系统。
          </p>
        </div>

        {/* Tab 控制器 */}
        <div
          className={`flex justify-center mb-6 md:mb-8 transition-all duration-700 delay-300 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center p-1 rounded-xl bg-white/60 border border-background-200/60">
            {threeAnswers.map((ans, i) => (
              <button
                key={ans.id}
                onClick={() => handleTab(i)}
                className={`relative px-5 py-2.5 rounded-lg font-body text-sm font-bold whitespace-nowrap transition-all duration-300 cursor-pointer ${
                  activeTab === i
                    ? 'text-white shadow-md'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
                style={
                  activeTab === i
                    ? { background: 'oklch(var(--primary-500))' }
                    : {}
                }
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono ${
                      activeTab === i ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-400'
                    }`}
                  >
                    {i + 1}
                  </span>
                  {ans.title}
                  {activeTab === i && <i className="ri-check-line text-xs" />}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* 内容区 */}
        <div className="max-w-5xl mx-auto">
          <div
            key={animKey}
            className="transition-all duration-500 opacity-100 translate-y-0"
          >
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
              {/* 左侧概览面板 */}
              <div className="md:col-span-4">
                <div className="rounded-xl bg-white/70 border border-background-200/60 h-full flex flex-col overflow-hidden glass-liquid">
                  <div className="h-1 w-full bg-gradient-to-r from-primary-300 to-gold-400" />

                  <div className="p-5 md:p-6 flex flex-col items-center text-center flex-1">
                    <div className="w-20 h-20 rounded-2xl bg-primary-50 border border-primary-200/40 flex items-center justify-center mb-5">
                      <i className={`${answer.icon} text-primary-600 text-4xl`} />
                    </div>

                    <h3 className="font-heading text-2xl font-bold text-foreground-800 mb-1.5">
                      {answer.title}
                    </h3>
                    <p className="text-xs text-foreground-400 font-body mb-4">
                      {answer.subtitle}
                    </p>

                    <p className="text-sm text-foreground-600 font-body leading-relaxed mb-6">
                      {answer.desc}
                    </p>

                    <div className="grid grid-cols-2 gap-3 w-full mt-auto">
                      {meta.highlights.map((h) => (
                        <div
                          key={h.label}
                          className={`py-3 px-2 rounded-lg text-center ${
                            h.color === 'accent'
                              ? 'bg-gold-50 border border-gold-200/40 glass-liquid-gold'
                              : 'bg-primary-50 border border-primary-200/40 glass-liquid'
                          }`}
                        >
                          <div
                            className={`number-font text-lg font-bold ${
                              h.color === 'accent' ? 'text-gold-600' : 'text-primary-600'
                            }`}
                          >
                            {h.num}
                          </div>
                          <div className="text-[10px] text-foreground-500 font-body mt-0.5">{h.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 右侧详情网格 */}
              <div className="md:col-span-8">
                <div className="rounded-xl bg-white/70 border border-background-200/60 h-full overflow-hidden glass-liquid">
                  <div className="h-1 w-full bg-gradient-to-r from-gold-400 to-primary-300" />
                  <div className="p-5 md:p-6 h-full flex flex-col">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1 content-stretch">
                      {meta.items.map((item, pi) => {
                        const isAccent = (item as { accent?: boolean }).accent;
                        const isLastSpan = isFiveItems && pi === 4;
                        return (
                          <div
                            key={item.num}
                            className={`group relative flex items-start gap-4 p-5 rounded-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.04)] h-full cursor-default ${
                              isLastSpan
                                ? 'sm:col-span-2 glass-liquid-gold border border-gold-200/50'
                                : 'glass-liquid border border-background-200/50 hover:border-gold-300/50'
                            }`}
                            style={{ animationDelay: `${pi * 80}ms` }}
                          >
                            {/* 左侧装饰竖条 */}
                            <div className={`absolute left-0 top-4 bottom-4 w-1.5 rounded-r transition-all duration-300 ${
                              isAccent ? 'bg-gold-400 group-hover:bg-gold-500' : 'bg-primary-300 group-hover:bg-primary-500'
                            }`} />

                            {/* 图标区域 */}
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              isAccent
                                ? 'bg-gold-400/15 border border-gold-200/40'
                                : 'bg-primary-50 border border-primary-200/40'
                            }`}>
                              <i className={`${item.icon} text-lg ${isAccent ? 'text-gold-500' : 'text-primary-500'}`} />
                            </div>

                            {/* 文字区域 */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-[11px] font-bold font-mono px-1.5 py-0.5 rounded ${
                                  isAccent
                                    ? 'bg-gold-100 text-gold-700'
                                    : 'bg-primary-100 text-primary-600'
                                }`}>
                                  {item.num}
                                </span>
                                <span className="text-sm font-body font-bold text-foreground-800">{item.title}</span>
                              </div>
                              <p className="text-xs text-foreground-500 font-body leading-relaxed">{item.desc}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}